// ============================================================
// 5. StockValuationReportPage.tsx
// ============================================================
// src/features/reports/pages/StockValuationReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getStockValuation, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Package, DollarSign, TrendingUp, Layers,
    Warehouse, Percent, BarChart3, PieChart
} from 'lucide-react';
import { motion } from 'framer-motion';

export function StockValuationReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['report-stock-valuation', tenantId, branchId],
        queryFn: () => getStockValuation(tenantId, branchId || undefined),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'stock_valuation', format, { branchId });
        window.open(url, '_blank');
    };

    const hasFilters = !!(branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
            <ReportHeader
                title="Stock Valuation"
                description="Inventory value analysis"
                icon={Warehouse}
                iconGradient="from-teal-500 to-emerald-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<Warehouse className="w-10 h-10" />}
                        title="No Stock Data"
                        description="No inventory data found. Try selecting a different branch."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Total Items"
                                value={String(data.summary?.totalItems || 0)}
                                icon={Package}
                                gradient="from-teal-500 to-emerald-600"
                            />
                            <SummaryCard
                                label="Total Quantity"
                                value={(data.summary?.totalQuantity || 0).toLocaleString()}
                                icon={Layers}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Cost Value"
                                value={formatNaira(data.summary?.totalCostValue || 0)}
                                icon={DollarSign}
                                gradient="from-slate-500 to-slate-600"
                            />
                            <SummaryCard
                                label="Retail Value"
                                value={formatNaira(data.summary?.totalRetailValue || 0)}
                                icon={TrendingUp}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Potential Profit"
                                value={formatNaira(data.summary?.totalPotentialProfit || 0)}
                                icon={Percent}
                                gradient="from-purple-500 to-violet-600"
                                comparison={data.summary?.overallMargin ? {
                                    value: parseFloat(data.summary.overallMargin),
                                    label: 'margin',
                                    isPositive: parseFloat(data.summary.overallMargin) > 30
                                } : undefined}
                            />
                        </div>

                        {/* Category Breakdown */}
                        {data.categoryBreakdown && data.categoryBreakdown.length > 0 && (
                            <Card>
                                <CardContent className="p-5">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-4">Category Breakdown</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {data.categoryBreakdown.map((cat: any, i: number) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                                className="bg-slate-50 rounded-xl p-4"
                                            >
                                                <p className="text-xs text-slate-400 uppercase tracking-wider">{cat.category}</p>
                                                <p className="text-lg font-bold text-slate-900 mt-1">{formatNaira(cat.retailValue)}</p>
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-xs text-slate-500">{cat.items} items</span>
                                                    <Badge variant="info" size="sm">
                                                        {cat.costValue > 0
                                                            ? ((parseFloat(cat.retailValue) - parseFloat(cat.costValue)) / parseFloat(cat.retailValue) * 100).toFixed(0)
                                                            : '0'}%
                                                    </Badge>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Stock Items Table */}
                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800">Stock Items</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Category</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Qty On Hand</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost Price</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost Value</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Retail Price</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Retail Value</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Margin</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {data.items?.map((item: any, i: number) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <span className="text-sm font-medium text-slate-900">{item.productName}</span>
                                                    <p className="text-xs text-slate-400">{item.branchName}</p>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">{item.categoryName}</td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold">
                                                    {parseFloat(item.quantityOnHand).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {formatNaira(item.costPrice)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                    {formatNaira(item.totalCostValue)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {formatNaira(item.retailPrice)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-emerald-600">
                                                    {formatNaira(item.totalRetailValue)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge
                                                        variant={parseFloat(item.margin) > 40 ? 'success' : parseFloat(item.margin) > 20 ? 'warning' : 'danger'}
                                                        size="sm"
                                                    >
                                                        {item.margin}%
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}